import {APIClient} from '../../../utils/APIClient';
import {isOk} from '../../../utils/apiResponse';
import {API_URL} from '../../../settings/local_settings';
import {LocalStorage} from '../../../utils/LocalStorage';

/**
 * Lo que el editor de Filtros GA4 le pregunta a GA4 mientras se arma un filtro. Las dos llamadas
 * son de sólo lectura y van por la PROPERTY elegida (no por la línea: en el wizard de la 2.1 la
 * línea puede no existir todavía).
 *
 * - `fetchGa4FiltersPreview`: cuánto capturan unos filtros SIN guardar y qué combinaciones entran.
 *   El backend usa la misma traducción que la sync, así que el número es el que después va a
 *   mostrar el Tablero. Con `adAccountId` (la cuenta de pauta de la fila, su pk) la respuesta trae
 *   además `coverage`: cuánto de eso se puede asignar a una campaña de esa cuenta.
 * - `fetchGa4DimensionValues`: los valores más usados de un campo, para autocompletar y para la
 *   pista de «ese valor es de otro campo». Se cachea en memoria por (property, campo): el backend
 *   ya lo cachea 24 h, y el editor lo pide cada vez que un chip toma foco.
 */

export const committedFilters = (filters) => (Array.isArray(filters) ? filters : [])
    .filter(f => f && Array.isArray(f.values) && f.values.some(v => v !== '' && v !== null && v !== undefined))
    .map(f => ({field: f.field, operator: f.operator, values: f.values}));

export function fetchGa4FiltersPreview(propertyId, filters, adAccountId) {
    const body = {filters: committedFilters(filters)};
    if (adAccountId !== null && adAccountId !== undefined && adAccountId !== '') body.ad_account_id = adAccountId;
    return new APIClient(`${API_URL}agency/ga4-properties/${propertyId}/filters-preview`).call('POST', body);
}

const VALUES_TTL_MS = 10 * 60 * 1000;
const valuesCache = new Map();

/**
 * Los valores del campo, o `null` si no se pudieron traer. `null` y no `[]`: un fallo NO es «la
 * property no tiene valores», y quien llama tiene que poder volver a pedir (C-GA4PREV-08). Tampoco
 * se cachea, para no dejar el autocompletar mudo 10 minutos por un blip.
 *
 * La clave lleva el token, como la del `APIClient`: el Map vive mientras viva la pestaña, y un
 * logout + login con otra cuenta no tiene que heredar los valores de la anterior.
 */
export function fetchGa4DimensionValues(propertyId, field) {
    const key = `${LocalStorage.fetchUserToken() || 'anon'}|${propertyId}:${field}`;
    const hit = valuesCache.get(key);
    if (hit && (hit.pending || Date.now() - hit.at < VALUES_TTL_MS)) {
        return hit.promise;
    }
    const promise = new APIClient(`${API_URL}agency/ga4-properties/${propertyId}/dimension-values?field=${field}`)
        .call('GET')
        .then(response => {
            if (!isOk(response) || !response.data || !Array.isArray(response.data.values)) {
                valuesCache.delete(key);
                return null;
            }
            valuesCache.set(key, {promise, at: Date.now(), pending: false});
            return response.data.values;
        })
        .catch(() => {
            valuesCache.delete(key);
            return null;
        });
    valuesCache.set(key, {promise, at: Date.now(), pending: true});
    return promise;
}

export function clearGa4DimensionValuesCache() {
    valuesCache.clear();
}
